import { DeleteAccountDialog } from '@avanta/shared/components/DeleteAccountDialog';
import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router';
import { PageHeader } from 'components/PageHeader';
import { ROUTES } from 'constants/routes';
import { ActivePatientRecord } from './components/ActivePatientRecord';
import { AppointmentsHistory } from './components/AppointmentsHistory';
import { useFetchAppointmentsHistory } from './components/AppointmentsHistory/useFetchAppointmentsHistory';
import { InsuredPersonProfileForm } from './components/InsuredPersonProfileForm';
import { PatientSummary } from './components/PatientSummary';
import { RecordsHistory } from './components/RecordsHistory';
import { useFetchReportsHistory } from './components/ReportsHistory/useFetchReportsHistory';
import { useFetchActivePatientRecord } from './hooks/useFetchActivePatientRecord';
import { useFetchPatientRecords } from './hooks/useFetchPatientRecords';

export const PatientProfile = (): React.ReactElement => {
  const params = useParams();
  const insuredPersonId = params?.id;
  const { showDialog } = useDialogContext();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { data: insuredPerson, isLoading: isLoadingPatient } = useFetchPatient(insuredPersonId);
  const { data: appointments = [], isLoading: isLoadingAppointments } = useFetchAppointmentsHistory(
    insuredPersonId ?? ''
  );
  const { data: activeRecord, isLoading: isLoadingActiveRecord } = useFetchActivePatientRecord(
    insuredPersonId ?? ''
  );
  const { data: records = [], isLoading: isLoadingRecords } = useFetchPatientRecords(
    insuredPersonId ?? ''
  );
  const { data: reports } = useFetchReportsHistory(insuredPersonId ?? '');

  if (!insuredPersonId) {
    return <Navigate to={ROUTES.PATIENTS} />;
  }

  if (isLoadingPatient || !insuredPerson) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex flex-col -mt-3 -mb-6 pb-8">
      <Button
        variant="ghost"
        className="text-teal-600 hover:text-teal-600 h-fit w-fit gap-1 p-0 hover:bg-transparent"
        onClick={() => navigate(ROUTES.PATIENTS)}
      >
        <ChevronLeft className="size-5" />
        {t('InsuredPersonsProfile.back')}
      </Button>
      <div className="mt-10">
        <PageHeader
          title={t('InsuredPersonsProfile.heading')}
          description={t('InsuredPersonsProfile.description')}
        />
      </div>
      <div className="mt-8 flex flex-col gap-12">
        <PatientSummary patient={insuredPerson} appointments={appointments} />
        <InsuredPersonProfileForm insuredPerson={insuredPerson} />
        <ActivePatientRecord record={activeRecord} isLoading={isLoadingActiveRecord} />
        <RecordsHistory
          records={records}
          appointments={appointments}
          reports={reports ?? []}
          isLoading={isLoadingRecords}
        />
        <AppointmentsHistory
          patientId={insuredPersonId}
          appointments={appointments}
          isLoading={isLoadingAppointments}
        />
        <Card
          variant="primary"
          title={t('RemoveInsuredPersonDialog.heading')}
          icon={Trash2}
          className="p-0 am-md:p-0"
        >
          <div className="flex flex-col gap-4 px-8 pt-6 pb-8 am-lg:flex-row am-lg:items-center am-lg:justify-between">
            <p className="text-denim-800 text-sm leading-4.5">
              {t('RemoveInsuredPersonDialog.body')}
            </p>
            <Button
              className="w-fit shrink-0"
              variant="destructive_terciary"
              size="lg"
              icon="leading"
              onClick={() =>
                showDialog({ component: <DeleteAccountDialog userId={insuredPersonId} /> })
              }
            >
              <Trash2 />
              {t('RemoveInsuredPersonDialog.cta')}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
